/**
 * Yaxle brand colors — the single source of truth for color in the app.
 *
 * Source: Yaxle Brand Guidelines v1.0 (docs/Yaxle_Brand_Guidelines.pdf).
 * Raw hex values live ONLY in this file. Everything else references these
 * scales or the semantic tokens in `../semanticTokens.ts`.
 */

/** Named brand colors exactly as published in the brand book. */
export const brandColors = {
	appleBlue: '#0071E3', // Primary 01 — leads the brand
	arcticTeal: '#2DD0C6', // Primary 02 — accent
	midnight: '#1A2733', // Primary 03 — branded/dark surfaces + on-accent text
	cloud: '#F5F5F7', // Primary 04 / Neutral 01 — canvas background
	pearl: '#E8E8EA', // Neutral 02 — cards, borders, quiet structure
	graphite: '#2E3338', // Neutral 04 — default body text
	smoke: '#8A8A8F', // Primary 05 — muted text
} as const;

/**
 * Sticky-note yellow — private "note to self" surfaces.
 * Not a brand primary; kept distinct from Apple Blue messages.
 */
export const note = {
	paper: '#FFF6BF',
	solid: '#FFE566',
	hover: '#F5D76E',
	deep: '#E6B800',
	ink: '#6B5314',
	inkHover: '#534010',
	muted: '#8A6D1A',
	border: '#E8C547',
} as const;

/** Apple Blue scale (brand). 500 is the published Apple Blue. */
export const brand = {
	50: '#E6F1FD',
	100: '#C0DCFA',
	200: '#97C5F7',
	300: '#6BADF2',
	400: '#3D8FEA',
	500: '#0071E3',
	600: '#005BC4',
	700: '#00469B',
	800: '#003271',
	900: '#001E47',
} as const;

/** Arctic Teal scale (accent). 500 is the published Arctic Teal. */
export const accent = {
	50: '#E7FAFA',
	100: '#C4F3F3',
	200: '#9CEBEB',
	300: '#6FE2E2',
	400: '#4CDADA',
	500: '#2DD0C6',
	600: '#25AAAA',
	700: '#1C8080',
	800: '#135656',
	900: '#0A2B2B',
} as const;

/**
 * Neutral scale derived from the Yaxle neutral palette:
 * Cloud -> Pearl -> Smoke -> Graphite -> Midnight.
 * Registered as `gray` so existing Chakra `gray.*` usage rebrands for free.
 */
export const neutral = {
	50: '#F5F5F7', // Cloud
	100: '#E8E8EA', // Pearl
	200: '#D9D9DC',
	300: '#C5C5C9',
	400: '#A6A6AB',
	500: '#8A8A8F', // Smoke
	600: '#6B6B70',
	700: '#4F4F54',
	800: '#2E3338', // Graphite
	900: '#1A2733', // Midnight
} as const;

/**
 * The full `colors` map handed to Chakra's theme.
 *
 * - `brand` / `appleBlue`: Apple Blue scale.
 * - `accent` / `arcticTeal`: Arctic Teal scale.
 * - `gray`: Yaxle neutrals (rebrands existing gray.* usage).
 * - `partbay-blue`: alias of the brand scale so the legacy
 *   `colorScheme="partbay-blue"` instantly inherits the Yaxle blue.
 * - Named single-stop tokens (`cloud`, `pearl`, ...) for semantic tokens.
 */
export const colors = {
	brand,
	appleBlue: brand,
	accent,
	arcticTeal: accent,
	gray: neutral,
	neutral,
	'partbay-blue': brand,
	cloud: brandColors.cloud,
	pearl: brandColors.pearl,
	smoke: brandColors.smoke,
	graphite: brandColors.graphite,
	midnight: brandColors.midnight,
	note,
} as const;

export default colors;
